import { Check, Camera } from "lucide-react";
import { IMAGES, DOMESTIC_PROCESS } from "./data";
import { scrollToId } from "./hooks";

const COMMERCIAL_AUDIENCES = [
  "Main contractors",
  "Construction companies",
  "Property developers",
  "Shopfitting companies",
  "Facilities management companies",
  "Commercial landlords",
  "Industrial operators",
  "Hotel groups",
  "Retail chains",
  "Property maintenance companies",
];

export function Commercial() {
  return (
    <section id="commercial" className="bg-[#182330] text-[#eef4f8] py-24 md:py-32 border-b border-[#213042]" data-testid="commercial-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-7 reveal">
            <span className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest block mb-2">// COMMERCIAL &amp; INDUSTRIAL</span>
            <h2 className="font-mono-code text-[clamp(26px,3.4vw,44px)] font-bold uppercase text-white mb-6 leading-tight">
              Your Reliable Partner For Commercial Mastic &amp; Silicone Contracts
            </h2>
            <p className="text-sm text-[#a2b8cc] leading-relaxed mb-4">
              We undertake substantial sealing packages for commercial and industrial projects of every scale.
              Our team can work around construction schedules, discuss technical specifications with your design
              team and provide detailed quotations for large commercial and industrial sealing works.
            </p>
            <p className="text-sm text-[#a2b8cc] leading-relaxed mb-8">
              Quality workmanship, reliability and attention to detail come as standard — with the capacity to
              deliver across a wide geographical area, from the Scottish Borders to John o&apos; Groats and down
              through North East England.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 mb-10">
              {COMMERCIAL_AUDIENCES.map((a) => (
                <div key={a} className="flex items-center gap-2.5">
                  <Check className="w-4 h-4 text-[#00c2a8] shrink-0" aria-hidden="true" />
                  <span className="font-mono-code text-xs text-white">{a}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => scrollToId("contact")}
              className="font-mono-code bg-[#00c2a8] text-[#111923] font-bold text-xs px-8 py-4 rounded uppercase tracking-wider hover:bg-white transition-colors"
              data-testid="commercial-quote-btn"
            >
              Discuss Your Project — Request A Commercial Quote
            </button>
          </div>

          <div className="lg:col-span-5 space-y-6 reveal">
            <figure className="border border-[#213042] rounded overflow-hidden">
              <img
                src={IMAGES.accessibleWc.src}
                alt={IMAGES.accessibleWc.alt}
                loading="lazy"
                className="w-full aspect-[4/3.4] object-cover"
                data-testid="commercial-image-1"
              />
              <figcaption className="bg-[#111923] px-4 py-2.5 font-mono-code text-[10px] uppercase tracking-widest text-[#00c2a8]">
                Our own operatives &bull; Live commercial premises
              </figcaption>
            </figure>
            <figure className="border border-[#213042] rounded overflow-hidden">
              <img
                src={IMAGES.commercialWc.src}
                alt={IMAGES.commercialWc.alt}
                loading="lazy"
                className="w-full aspect-[4/3.4] object-cover"
                data-testid="commercial-image-2"
              />
              <figcaption className="bg-[#111923] px-4 py-2.5 font-mono-code text-[10px] uppercase tracking-widest text-[#00c2a8]">
                Completed washroom package &bull; Precise silicone lines
              </figcaption>
            </figure>
          </div>
        </div>
      </div>
    </section>
  );
}

export function Domestic() {
  return (
    <section id="domestic" className="bg-[#111923] text-[#eef4f8] py-24 md:py-32 border-b border-[#213042]" data-testid="domestic-section">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5 space-y-6 reveal">
            <figure className="border border-[#213042] rounded overflow-hidden">
              <img
                src={IMAGES.showerStrip.src}
                alt={IMAGES.showerStrip.alt}
                loading="lazy"
                className="w-full aspect-[4/4.2] object-cover"
                data-testid="domestic-image-1"
              />
              <figcaption className="bg-[#182330] px-4 py-2.5 font-mono-code text-[10px] uppercase tracking-widest text-[#00c2a8]">
                Careful removal of failed sealant
              </figcaption>
            </figure>
            <figure className="border border-[#213042] rounded overflow-hidden">
              <img
                src={IMAGES.showerTray.src}
                alt={IMAGES.showerTray.alt}
                loading="lazy"
                className="w-full aspect-[4/3] object-cover"
                data-testid="domestic-image-2"
              />
              <figcaption className="bg-[#182330] px-4 py-2.5 font-mono-code text-[10px] uppercase tracking-widest text-[#00c2a8]">
                The finished result &bull; Crisp, clean lines
              </figcaption>
            </figure>
          </div>


          <div className="lg:col-span-7 reveal">
            <span className="font-mono-code text-xs text-[#00c2a8] uppercase tracking-widest block mb-2">// DOMESTIC RESEALING</span>
            <h2 className="font-mono-code text-[clamp(26px,3.4vw,44px)] font-bold uppercase text-white mb-6 leading-tight">
              Give Your Bathroom Or Kitchen A Professional Finish
            </h2>
            <p className="text-sm text-[#a2b8cc] leading-relaxed mb-4">
              Old, discoloured, cracked or mouldy silicone can make an otherwise attractive bathroom or kitchen
              look tired. Our professional resealing services provide clean, neat finishes using appropriate
              high-quality materials — the same standard of work we deliver on commercial contracts.
            </p>

            <ol className="space-y-4 mb-10">
              {DOMESTIC_PROCESS.map((step, i) => (
                <li key={i} className="flex items-start gap-4">
                  <span className="font-mono-code text-xs font-bold text-[#111923] bg-[#00c2a8] rounded w-6 h-6 flex items-center justify-center shrink-0 mt-0.5">
                    {i + 1}
                  </span>
                  <span className="text-sm text-[#a2b8cc] leading-relaxed">{step}</span>
                </li>
              ))}
            </ol>

            <div className="flex flex-wrap gap-4">
              <a
                href="https://wa.me/447796225041?text=Hello%2C%20I%27d%20like%20a%20free%20quote%20for%20a%20reseal.%20I%27ll%20attach%20photos."
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-mono-code bg-[#00c2a8] text-[#111923] font-bold text-xs px-8 py-4 rounded uppercase tracking-wider hover:bg-white transition-colors"
                data-testid="domestic-photos-btn"
              >
                <Camera className="w-4 h-4" aria-hidden="true" />
                Send Us Photos For A Free Quote
              </a>
              <button
                onClick={() => scrollToId("contact")}
                className="font-mono-code border border-[#30455e] text-white text-xs px-8 py-4 rounded uppercase tracking-wider hover:border-[#00c2a8] transition-colors"
                data-testid="domestic-quote-btn"
              >
                Use The Quote Form
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
